/**
 * Indicators step.
 *
 * Left: the widget list plus the "Add Indicator" gallery.
 * Right: the selected widget's configuration, with a LIVE preview rendered by
 * the same `WidgetRenderer` the published dashboard uses — so what is being
 * configured is the real widget pulling real data, not a mock.
 */

import { useState } from 'react';
import { Copy, Plus, Trash2, X } from 'lucide-react';
import { createId } from '../services/storage';
import { createWidget } from './services/dashboardService';
import WidgetConfigForm from './components/WidgetConfigForm';
import WidgetRenderer from './components/WidgetRenderer';
import { packWidgets, resolveCollisions, validateWidget } from './utils/dashboardUtils';
import { dashboardModuleStub, dashboardSourceAsLayer } from './utils/layerAdapter';
import { WIDGET_LIBRARY, type DashboardConfig, type DashboardWidget, type WidgetType } from './types/dashboardTypes';
import SymbologyPanel from '../components/layers/symbology/SymbologyPanel';
import LabelProperties from '../labeling/LabelProperties';
import { scaleAt } from '../labeling/ScaleVisibility';
import type { LayerRenderer } from '../types';
import type { LayerLabeling } from '../labeling/LabelTypes';

interface Props {
  config: DashboardConfig;
  onChange: (config: DashboardConfig) => void;
  selectedWidgetId: string | null;
  onSelectWidget: (id: string | null) => void;
}

export default function DashboardIndicators({
  config,
  onChange,
  selectedWidgetId,
  onSelectWidget,
}: Props) {
  const [showGallery, setShowGallery] = useState(false);
  /** Which widget/data-source's Symbology or Labels editor is open over the
   *  live preview, if any — see WidgetConfigForm's `onStyleSource`. Keeps
   *  its OWN widget id rather than trusting `selectedWidgetId` at apply
   *  time, so switching the selected widget while the editor is open can
   *  never write a style onto the wrong one. */
  const [styleTarget, setStyleTarget] = useState<{
    widgetId: string;
    dataSourceId: string;
    mode: 'symbology' | 'labels';
  } | null>(null);

  const selected = config.widgets.find((widget) => widget.id === selectedWidgetId) ?? null;
  const styleWidget = styleTarget
    ? config.widgets.find((widget) => widget.id === styleTarget.widgetId) ?? null
    : null;
  const styleSource =
    styleTarget && styleWidget
      ? config.dataSources.find((entry) => entry.id === styleTarget.dataSourceId) ?? null
      : null;

  const addWidget = (type: WidgetType) => {
    const widget = createWidget(type, config.widgets);
    // Flow the new tile into the current row instead of dropping it onto a row
    // of its own. Widths are left alone here — only "Auto arrange" resizes what
    // the admin already sized.
    onChange({ ...config, widgets: packWidgets([...config.widgets, widget]) });
    onSelectWidget(widget.id);
    setShowGallery(false);
  };

  const updateWidget = (updated: DashboardWidget) =>
    onChange({
      ...config,
      widgets: config.widgets.map((widget) => (widget.id === updated.id ? updated : widget)),
    });

  /** Patches one data source's entry in a map widget's `mapLayerStyles`. */
  const applyMapLayerStyle = (
    widget: DashboardWidget,
    dataSourceId: string,
    patch: { renderer?: LayerRenderer; labeling?: LayerLabeling },
  ) => {
    const current = widget.config.mapLayerStyles ?? {};
    updateWidget({
      ...widget,
      config: {
        ...widget.config,
        mapLayerStyles: { ...current, [dataSourceId]: { ...current[dataSourceId], ...patch } },
      },
    });
  };

  const deleteWidget = (id: string) => {
    onChange({ ...config, widgets: config.widgets.filter((widget) => widget.id !== id) });
    if (selectedWidgetId === id) onSelectWidget(null);
  };

  const duplicateWidget = (id: string) => {
    const original = config.widgets.find((widget) => widget.id === id);
    if (!original) return;

    const copy: DashboardWidget = {
      ...original,
      id: createId('wgt'),
      title: `${original.title} (copy)`,
      // Deep-copy the parts a shallow spread would still share.
      filters: original.filters.map((clause) => ({ ...clause, id: createId('flt') })),
      config: { ...original.config },
      layout: { ...original.layout, y: original.layout.y + original.layout.h },
    };

    onChange({
      ...config,
      widgets: resolveCollisions([...config.widgets, copy], copy.id),
    });
    onSelectWidget(copy.id);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
      {/* ── Widget list ─────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">
            Widgets ({config.widgets.length})
          </h2>
          <button
            type="button"
            onClick={() => setShowGallery(true)}
            className="gl-btn gl-btn--primary px-2.5 py-1.5 text-xs"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Widget
          </button>
        </div>

        {config.widgets.length === 0 ? (
          <div className="gl-panel border-dashed py-10 text-center">
            <p className="text-sm text-slate-500">No widgets yet</p>
            <p className="text-xs text-slate-400">
              Add a KPI, chart, table, or map to get started.
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {config.widgets.map((widget) => {
              const problems = validateWidget(widget);
              const active = widget.id === selectedWidgetId;
              const source = config.dataSources.find((entry) => entry.id === widget.dataSourceId);

              return (
                <li key={widget.id}>
                  <div
                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 transition ${
                      active
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectWidget(widget.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="block truncate text-xs font-medium text-slate-800">
                        {widget.title}
                      </span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {WIDGET_LIBRARY.find((entry) => entry.type === widget.type)?.label}
                        {source ? ` · ${source.name}` : ''}
                      </span>
                    </button>

                    {problems.length > 0 && (
                      <span
                        title={problems.join('\n')}
                        className="flex-shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                      >
                        Setup
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={() => duplicateWidget(widget.id)}
                      aria-label={`Duplicate ${widget.title}`}
                      title="Duplicate"
                      className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteWidget(widget.id)}
                      aria-label={`Delete ${widget.title}`}
                      title="Delete"
                      className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Configuration + live preview ────────────────────────────────── */}
      <div>
        {!selected ? (
          <div className="flex h-full min-h-[240px] items-center justify-center gl-panel border-dashed">
            <p className="text-sm text-slate-400">
              {config.widgets.length === 0
                ? 'Add a widget to configure it.'
                : 'Select a widget to configure it.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,300px)]">
            <div className="gl-panel p-4">
              <WidgetConfigForm
                widget={selected}
                dataSources={config.dataSources}
                onChange={updateWidget}
                // Needed so the Actions section can list the other widgets.
                allWidgets={config.widgets}
                onStyleSource={(dataSourceId, mode) =>
                  setStyleTarget({ widgetId: selected.id, dataSourceId, mode })
                }
              />
            </div>

            <div className="relative">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Live preview
              </p>
              <div style={{ height: Math.max(200, selected.layout.h * 72) }}>
                {/* Keyed on the configuration so a changed field re-mounts the
                    widget and re-runs its query immediately. */}
                <WidgetRenderer
                  key={`${selected.id}|${JSON.stringify(selected.config)}|${JSON.stringify(
                    selected.filters,
                  )}|${selected.dataSourceId ?? ''}`}
                  widget={selected}
                  dataSources={config.dataSources}
                  refreshInterval={0}
                  accentColor={config.theme.accentColor}
                  dividerColor={config.theme.dividerColor}
                />
              </div>

              {/* Float the SAME Symbology/Label editors the main Application
                  Builder uses, right over the live preview — the real map a
                  style change should be seen on, not the narrow config
                  sidebar. Both write straight through (no separate preview
                  draft): every tweak lands on the widget immediately,
                  matching how every other field in the config form already
                  behaves here. */}
              {styleWidget && styleSource && (
                <>
                  <SymbologyPanel
                    layer={dashboardSourceAsLayer(styleSource, styleWidget.config.mapLayerStyles?.[styleSource.id])}
                    module={dashboardModuleStub(styleSource)}
                    open={styleTarget?.mode === 'symbology'}
                    onPreview={(renderer) => applyMapLayerStyle(styleWidget, styleSource.id, { renderer })}
                    onApply={(renderer) => applyMapLayerStyle(styleWidget, styleSource.id, { renderer })}
                    onClose={() => setStyleTarget(null)}
                  />
                  <LabelProperties
                    layer={dashboardSourceAsLayer(styleSource, styleWidget.config.mapLayerStyles?.[styleSource.id])}
                    module={dashboardModuleStub(styleSource)}
                    open={styleTarget?.mode === 'labels'}
                    currentScale={scaleAt(
                      (styleWidget.config.center ?? [28.7041, 77.1025])[0],
                      styleWidget.config.zoom ?? 11,
                    )}
                    onPreview={(labeling) =>
                      applyMapLayerStyle(styleWidget, styleSource.id, { labeling: labeling ?? undefined })
                    }
                    onApply={(labeling) => applyMapLayerStyle(styleWidget, styleSource.id, { labeling })}
                    onClose={() => setStyleTarget(null)}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Widget gallery ──────────────────────────────────────────────── */}
      {showGallery && (
        <div
          className="gl-modal-backdrop fixed inset-0 z-[9000] flex items-center justify-center px-4"
          onClick={() => setShowGallery(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Add widget"
            className="gl-modal max-h-[80vh] w-full max-w-2xl overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
              <h3 className="text-sm font-semibold text-slate-900">Add widget</h3>
              <button
                type="button"
                onClick={() => setShowGallery(false)}
                aria-label="Close"
                className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid max-h-[calc(80vh-56px)] grid-cols-2 gap-2 overflow-y-auto p-4 sm:grid-cols-3">
              {WIDGET_LIBRARY.map((entry) => (
                <button
                  key={entry.type}
                  type="button"
                  onClick={() => addWidget(entry.type)}
                  className="rounded-xl border border-slate-200 p-3 text-left transition hover:border-blue-400 hover:bg-blue-50"
                >
                  <span className="block text-xs font-semibold text-slate-800">{entry.label}</span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                    {entry.description}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
